import gsap from "gsap";
import reveal from "./reveal";
import popups from "./popups";
import observe from "./observe";

const LINES = '.payment-summary__line, .payment-summary__line--total, .payment-summary__line--savings';
const NOTICES = '.woocommerce-NoticeGroup, .woocommerce-error, .woocommerce-message, .woocommerce-info';
const FLASH = 'rgba(183, 226, 69, 0.45)';
const FADED = 'rgba(183, 226, 69, 0)';

const finals = new WeakMap();

const checkout = {
    values: new Map(),
    timer: 0,

    init() {
        const form = document.querySelector('form.woocommerce-checkout');
        const cart = document.querySelector('.woocommerce-cart-form');
        const root = form || (cart ? cart.parentElement : null);

        if (!root) {
            return;
        }

        this.values = this.read();
        new MutationObserver((mutations) => this.mutated(mutations)).observe(root, { childList: true, subtree: true });
        document.addEventListener('change', (event) => this.picked(event), true);
        document.addEventListener('click', (event) => this.toggled(event), true);

        if (form) {
            form.querySelectorAll('.form-row').forEach((row) => {
                observe.classChange(row, 'woocommerce-invalid', (invalid) => {
                    if (invalid) {
                        this.shake(row);
                    }
                });
            });
        }
    },

    key(node) {
        return node.id ? '#' + node.id : node.classList[0];
    },

    mutated(mutations) {
        const removed = [];
        const added = [];

        mutations.forEach((mutation) => {
            mutation.removedNodes.forEach((node) => node.nodeType === 1 && removed.push(node));
            mutation.addedNodes.forEach((node) => node.nodeType === 1 && added.push(node));
        });

        removed.forEach((node) => {
            const key = this.key(node);
            const index = added.findIndex((candidate) => candidate.isConnected && this.key(candidate) === key);

            if (key && !key.startsWith('ex-') && index > -1) {
                reveal.swap(node, added[index]);
                added.splice(index, 1);
            }
        });

        added.filter((node) => node.matches(NOTICES)).forEach((node) => {
            gsap.fromTo(node, { opacity: 0, y: -24, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'back.out(1.6)', clearProps: 'opacity,transform' });
        });

        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.settle(), 30);
    },

    read() {
        const values = new Map();

        document.querySelectorAll('.order-summary-details').forEach((box) => {
            box.querySelectorAll(LINES).forEach((line) => {
                const label = line.firstElementChild ? line.firstElementChild.textContent.replace(/[\d\s().,]+/g, ' ').trim() : '';
                const value = line.querySelector('.payment-summary__value');

                if (value && !values.has(label)) {
                    values.set(label, { line, value, text: finals.get(value) || value.textContent.trim() });
                }
            });
        });

        return values;
    },

    settle() {
        const values = this.read();

        values.forEach((current, label) => {
            const previous = this.values.get(label);

            if (previous && previous.text !== current.text) {
                this.changed(current, popups.price(previous.text));
            }
        });

        this.values = values;
    },

    changed({ line, value, text }, from) {
        finals.set(value, text);

        if (from !== null) {
            popups.countUp(value, 0, from);
        }

        gsap.fromTo(line, { backgroundColor: FLASH, boxShadow: '0 0 0 8px ' + FLASH, borderRadius: 6 }, {
            backgroundColor: FADED,
            boxShadow: '0 0 0 8px ' + FADED,
            duration: 1.4,
            ease: 'power2.out',
            clearProps: 'backgroundColor,boxShadow,borderRadius',
        });

        if (line.matches('.payment-summary__line--total')) {
            gsap.fromTo(value, { scale: 1.18, transformOrigin: '100% 50%' }, { scale: 1, duration: 0.9, ease: 'elastic.out(1, 0.45)', clearProps: 'transform,transformOrigin' });
        }
    },

    picked(event) {
        const input = event.target;

        if (!input.matches) {
            return;
        }

        if (input.matches('#ship-to-different-address-checkbox') && input.checked) {
            const rows = document.querySelectorAll('.shipping_address .form-row');

            gsap.fromTo(rows, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.6, stagger: 0.05, delay: 0.1, clearProps: 'opacity,transform' });
            return;
        }

        if (!input.matches('input.shipping_method, input[name="payment_method"]')) {
            return;
        }

        const row = input.closest('li');
        const icon = row ? row.querySelector('.shipping-method__icon') : null;

        if (!row) {
            return;
        }

        row.classList.remove('ex-picked');
        window.requestAnimationFrame(() => row.classList.add('ex-picked'));
        setTimeout(() => row.classList.remove('ex-picked'), 700);

        if (icon) {
            gsap.fromTo(icon, { scale: 0.7, rotation: -14 }, { scale: 1, rotation: 0, duration: 0.6, ease: 'back.out(3)', clearProps: 'transform' });
        } else {
            gsap.fromTo(row.querySelectorAll('.payment-method__title'), { x: 14 }, { x: 0, duration: 0.6, ease: 'back.out(2.5)', clearProps: 'transform' });
        }
    },

    toggled(event) {
        const toggle = event.target.closest ? event.target.closest('.coupon-toggle, .checkout-products-toggle') : null;
        const section = toggle ? toggle.closest('.cart-coupon-section, .checkout-products-section') : null;
        const box = section ? section.querySelector('.coupon-form, .checkout-products-list') : null;

        if (!box) {
            return;
        }

        const wasOpen = window.getComputedStyle(box).display !== 'none';

        window.requestAnimationFrame(() => {
            const open = window.getComputedStyle(box).display !== 'none';
            const icon = Array.from(toggle.querySelectorAll('svg')).find((svg) => window.getComputedStyle(svg).display !== 'none');

            if (open === wasOpen) {
                return;
            }

            if (icon) {
                gsap.fromTo(icon, { rotation: -180, scale: 0.4, transformOrigin: '50% 50%' }, { rotation: 0, scale: 1, duration: 0.5, ease: 'back.out(2)', clearProps: 'transform,transformOrigin' });
            }

            if (!open || (window.jQuery && window.jQuery(box).is(':animated'))) {
                return;
            }

            gsap.fromTo(box, { height: 0, opacity: 0, overflow: 'hidden' }, { height: 'auto', opacity: 1, duration: 0.5, ease: 'power3.out', clearProps: 'height,opacity,overflow' });
            gsap.fromTo(box.children, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.5, stagger: 0.06, delay: 0.1, clearProps: 'opacity,transform' });
        });
    },

    shake(row) {
        const field = row.querySelector('.input-text, select:not(.select2-hidden-accessible), .select2-selection, input[type="checkbox"]') || row;

        gsap.fromTo(field, { x: 0 }, { keyframes: { x: [-9, 8, -6, 4, -2, 0] }, duration: 0.5, ease: 'power1.out', clearProps: 'transform' });
    },
};

export default checkout;
